import React from 'react';
import { Button, Message, useToaster } from 'rsuite';
import * as blogPostsApi from '../../api/blogPosts';
import { uploadFormData } from '../../api/request';
import { API_DOMAIN } from '../../config';
import { useConfirm } from '../../common/useConfirm';

type Props = {
  postId: string;
};

type GalleryImage = {
  id: string;
  postId: string;
  imageUrl: string;
  width: number;
  height: number;
  sortOrder: number;
  createdAt?: number | null;
  updatedAt?: number | null;
  alt?: string | null;
  altEn?: string | null;
  altDe?: string | null;
};

type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

type UploadItem = {
  id: string;
  file: File;
  status: UploadStatus;
  error?: string;
};

const getImageDimensions = (file: File) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      URL.revokeObjectURL(objectUrl);
      if (!width || !height) {
        reject(new Error('Не вдалося визначити розміри зображення'));
        return;
      }
      resolve({ width, height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Не вдалося зчитати зображення'));
    };
    image.src = objectUrl;
  });

const readResponseError = async (response: Response) => {
  try {
    const payload = await response.json();
    return (
      payload?.message?.message ||
      payload?.message ||
      payload?.error ||
      'Помилка завантаження'
    );
  } catch (err) {
    try {
      const text = await response.text();
      return text || 'Помилка завантаження';
    } catch (error) {
      return 'Помилка завантаження';
    }
  }
};

const createUploadId = () =>
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getUploadStatusLabel = (status: UploadStatus) => {
  switch (status) {
    case 'pending':
      return 'Очікує';
    case 'uploading':
      return 'Завантаження...';
    case 'success':
      return 'Готово';
    case 'error':
      return 'Помилка';
    default:
      return '';
  }
};

export default function BlogPostGallerySection({ postId }: Props) {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  const [images, setImages] = React.useState<GalleryImage[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [uploads, setUploads] = React.useState<UploadItem[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const loadGallery = React.useCallback(async () => {
    if (!postId) {
      return;
    }
    setLoading(true);
    try {
      const response = await blogPostsApi.listGallery(postId);
      setImages(response.data || []);
    } catch (err) {
      console.error(err);
      toaster.push(
        <Message type="error" showIcon closable>
          Не вдалося завантажити галерею
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    } finally {
      setLoading(false);
    }
  }, [postId, toaster]);

  React.useEffect(() => {
    loadGallery().catch(console.error);
  }, [loadGallery]);

  const updateUploadItem = (id: string, patch: Partial<UploadItem>) => {
    setUploads(prev =>
      prev.map(item => (item.id === id ? { ...item, ...patch } : item))
    );
  };

  const uploadFilesSequentially = async (queue: UploadItem[]) => {
    if (!postId) {
      return;
    }
    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const item of queue) {
      if (item.status === 'error') {
        errorCount += 1;
        continue;
      }
      updateUploadItem(item.id, { status: 'uploading', error: undefined });
      try {
        const { width, height } = await getImageDimensions(item.file);
        const formData = new FormData();
        formData.append('file', item.file);
        const uploadResponse = await uploadFormData(
          `${API_DOMAIN}/api/uploads/blog-gallery`,
          formData
        );

        if (!uploadResponse.ok) {
          const message = await readResponseError(uploadResponse);
          throw new Error(message);
        }

        const uploadPayload = await uploadResponse.json();
        const imageUrl = uploadPayload?.url as string | undefined;
        if (!imageUrl) {
          throw new Error('Не вдалося отримати посилання на файл');
        }

        await blogPostsApi.addGalleryImage(postId, {
          imageUrl,
          width,
          height,
        });
        updateUploadItem(item.id, { status: 'success' });
        successCount += 1;
      } catch (err: any) {
        const message =
          err instanceof Error
            ? err.message
            : 'Помилка завантаження зображення';
        updateUploadItem(item.id, { status: 'error', error: message });
        errorCount += 1;
      }
    }

    setUploading(false);
    await loadGallery();

    if (successCount > 0) {
      toaster.push(
        <Message type="success" showIcon closable>
          Завантажено {successCount} фото
        </Message>,
        { placement: 'topEnd', duration: 1500 }
      );
    }
    if (errorCount > 0) {
      toaster.push(
        <Message type="warning" showIcon closable>
          Не вдалося завантажити {errorCount} фото
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (uploading) {
      return;
    }
    const files: File[] = event.target.files
      ? Array.from(event.target.files)
      : [];
    event.target.value = '';
    if (!files.length) {
      return;
    }

    const nextUploads: UploadItem[] = files.map(file => {
      if (!file.type.startsWith('image/')) {
        return {
          id: createUploadId(),
          file,
          status: 'error',
          error: 'Файл не є зображенням',
        };
      }
      return {
        id: createUploadId(),
        file,
        status: 'pending',
      };
    });

    setUploads(nextUploads);
    await uploadFilesSequentially(nextUploads);
  };

  const handleRemove = async (imageId: string) => {
    if (!postId) {
      return;
    }
    const result = await confirm({
      message: 'Видалити це зображення?',
      destructive: true,
      confirmText: 'Видалити',
    });
    if (!result) {
      return;
    }
    try {
      await blogPostsApi.removeGalleryImage(postId, imageId);
      setImages(prev => prev.filter(image => image.id !== imageId));
      toaster.push(
        <Message type="success" showIcon closable>
          Зображення видалено
        </Message>,
        { placement: 'topEnd', duration: 1500 }
      );
    } catch (err) {
      console.error(err);
      toaster.push(
        <Message type="error" showIcon closable>
          Не вдалося видалити зображення
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    }
  };

  return (
    <div className="blog-gallery">
      {ConfirmDialog}
      <div className="form-modal__card form-modal__card--muted blog-gallery__drop">
        <div className="form-modal__inline">
          <Button
            appearance="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Додати фото
          </Button>
          <span className="form-modal__hint">Можна вибрати кілька файлів</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        {uploads.length > 0 ? (
          <div className="blog-gallery__upload-list">
            {uploads.map(item => (
              <div
                key={item.id}
                className={`blog-gallery__upload-item blog-gallery__upload-item--${item.status}`}
              >
                <span className="blog-gallery__upload-name">
                  {item.file.name}
                </span>
                <span className="blog-gallery__upload-status">
                  {getUploadStatusLabel(item.status)}
                </span>
                {item.error ? (
                  <span className="blog-gallery__upload-error">
                    {item.error}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {loading ? (
        <span className="form-modal__hint">Завантаження галереї...</span>
      ) : images.length === 0 ? (
        <span className="form-modal__hint">Зображень поки немає</span>
      ) : (
        <div className="blog-gallery__grid">
          {images.map(image => (
            <div key={image.id} className="blog-gallery__item">
              <img
                src={image.imageUrl}
                alt={image.alt || 'Gallery image'}
                className="blog-gallery__image"
              />
              <div className="blog-gallery__meta">
                <span>
                  {image.width}×{image.height}
                </span>
                <span>Порядок: {image.sortOrder}</span>
              </div>
              <div className="blog-gallery__actions">
                <Button
                  appearance="link"
                  onClick={() => window.open(image.imageUrl, '_blank')}
                >
                  Переглянути
                </Button>
                <Button
                  appearance="subtle"
                  className="delete-btn"
                  onClick={() => handleRemove(image.id)}
                >
                  Видалити
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
